import { Component, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { ModalQuoteChooseProjectComponent } from 'app/modal/modal-quote-choose-project/modal-quote-choose-project.component';
import { CoreQuoteForwardModalComponent } from '../core-quote-forward-modal/core-quote-forward-modal.component';
import { CoreQuoteReviewResultModalComponent } from '../core-quote-review-result-modal/core-quote-review-result-modal.component';
import { QuoteService } from 'app/quote/quote.service';
import { ModalQuoteAcceptComponent } from 'app/modal/modal-quote-accept/modal-quote-accept.component';
import { ModalQuoteMarkAsSentComponent } from 'app/modal/modal-quote-mark-as-sent/modal-quote-mark-as-sent.component';
import { ModalCrmLoseComponent } from 'app/modal/modal-crm-lose/modal-crm-lose.component';
import { sendDealProjectChange } from 'app/quote/quote-deal-project';

const QUOTE_ERROR_DEAL_LOST_REASON = 22;

@Component({
	selector: 'app-core-quote-header',
	templateUrl: './core-quote-header.component.html'
})
export class CoreQuoteHeaderComponent implements OnInit, OnChanges, OnDestroy {

	@Input() quoteId;

	@ViewChild(CoreQuoteForwardModalComponent) quoteForwardModal: CoreQuoteForwardModalComponent;
	@ViewChild(ModalQuoteAcceptComponent) quoteAcceptModal: ModalQuoteAcceptComponent;
	@ViewChild(CoreQuoteReviewResultModalComponent) reviewResultModal: CoreQuoteReviewResultModalComponent;
	@ViewChild(ModalQuoteChooseProjectComponent) chooseProjectModal: ModalQuoteChooseProjectComponent;
	@ViewChild(ModalQuoteMarkAsSentComponent) quoteMarkAsSentModal: ModalQuoteMarkAsSentComponent;
	@ViewChild(ModalCrmLoseComponent) loseModal: ModalCrmLoseComponent;

	id;
	data;
	refreshTimer;

	editedTitle = '';
	titleEdit = false;

	disabled = false;

	private sub;

	get showReviewResult() { return this.app.orgInfo?.role.can_review_quotes && this.data?.stage === 'in_review'; }

	constructor(
		public app: AppService,
		private api: ApiService,
		public service: QuoteService
	) { }

	ngOnInit() {
		this.sub = this.app.quotePriceChange.subscribe(() => {
			this.scheduleRefresh();
		});
	}

	ngOnChanges() {
		if (this.quoteId !== this.id) {
			// Quote ID has changed
			this.id = this.quoteId;
			this.data = null;
			this.refresh();
		}
	}

	ngOnDestroy() {
		clearTimeout(this.refreshTimer);
		if (this.sub) this.sub.unsubscribe();
	}

	refresh() {
		clearTimeout(this.refreshTimer);
		if (!this.id) return;

		this.api.quote.headerSummary(this.id, data => {
			this.disabled = false;
			this.data = data;

			if (['accepted'].includes(this.data.stage) && this.app.routeData.module === 'quote' && !this.data.project_id && this.data.set_project_counter < 2) {
				this.chooseProjectModal.open({
					customerId: this.data.customer_id,
					currencyId: this.data.currency_id
				});
			}

			if (['complete'].includes(this.data.stage) && this.app.routeData.module === 'sales-order' && !this.data.project_id && this.data.set_project_counter < 2) {
				this.chooseProjectModal.open({
					customerId: this.data.customer_id,
					currencyId: this.data.currency_id
				});
			}
		}, () => {
			this.disabled = false;
		});
	}

	scheduleRefresh() {
		clearTimeout(this.refreshTimer);
		this.refreshTimer = setTimeout(() => this.refresh(), 2000);
	}

	changeStage(s, lostReason: string = null) {
		if (!this.id) return;

		if (this.app.routeData.module === 'quote') {
			this.disabled = true;
			this.api.quote.changeStage(this.id, s, res => {
				this.refresh();
				this.app.quoteStageChange.emit(s);

				// Accepting a quote that belongs to a deal rejects the deal's other quotes, since
				// they are either/or alternatives for the same job (D-005). It happens either way;
				// this is so it does not happen silently on a screen that never mentions the deal.
				if (res?.deal_alternatives_cancelled) {
					this.app.notifications.showSuccess(res.deal_alternatives_cancelled === 1
						? 'The other quote on this deal has been rejected.'
						: 'The other ' + res.deal_alternatives_cancelled + ' quotes on this deal have been rejected.');
				}

				// The deal's stage is read from its quotes, so this may have moved it. Said out
				// loud on a screen that never mentions the deal otherwise (D-069).
				if (res?.deal_stage) {
					this.app.notifications.showSuccess('The deal moved to ' + res.deal_stage + '.');
				}
			}, error => {
				this.disabled = false;

				switch (error.code) {
					case 4:
					case 1:
						this.quoteForwardModal.open({ id: this.id, mode: error.code === 1 ? 'send' : 'review' });
						break;

					case 3:
						this.quoteAcceptModal.open({ quoteId: this.id });
						break;

					case QUOTE_ERROR_DEAL_LOST_REASON:
						this.loseModal.open({ quote: true, action: 'cancel', title: error.payload?.title, stage: error.payload?.stage,
							billed: error.payload?.billed, currencyId: error.payload?.currency_id });
						break;

					default:
						this.app.notifications.showDanger(error.message);
						break;
				}
			}, lostReason);
		} else if (this.app.routeData.module === 'sales-order') {
			this.disabled = true;
			this.api.quote.changeSalesOrderStage(this.id, s, () => {
				this.refresh();
				this.app.quoteStageChange.emit(s);
			}, error => {
				this.disabled = false;

				switch (error.code) {
					case 3:
						this.quoteMarkAsSentModal.open({ quoteId: this.id });
						break;

					case QUOTE_ERROR_DEAL_LOST_REASON:
						this.loseModal.open({ quote: true, action: 'cancel', title: error.payload?.title, stage: error.payload?.stage,
							billed: error.payload?.billed, currencyId: error.payload?.currency_id });
						break;

					default:
						this.app.notifications.showDanger(error.message);
						break;
				}
			}, lostReason);
		}

	}

	// Kept quote: nothing was cancelled. A reason: the same cancel again, carrying it.
	loseModalClosed(result) {
		if (result?.reason) this.changeStage('cancelled', result.reason);
	}

	editTitle() {
		// Starts from what the header shows, which is the Name until a title is set
		this.editedTitle = this.data.title || this.data.description || '';
		this.titleEdit = true;
	}

	cancelTitle() {
		this.titleEdit = false;
	}

	saveTitle() {
		// Untouched Name fallback: nothing to save, so quote.title stays empty
		if (!this.data.title && (this.editedTitle || '') === (this.data.description || '')) {
			this.titleEdit = false;
			return;
		}

		this.disabled = true;
		this.api.quote.changeTitle(this.id, this.editedTitle, () => {
			this.titleEdit = false;
			this.refresh();
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	stageList() {
		if (this.app.routeData.module === 'quote') {
			if (this.app.orgInfo?.use_quote_review) {
				return ['in_progress', 'in_review', 'passed_review', 'sent', 'accepted', 'complete', 'cancelled'];
			} else {
				return ['in_progress', 'sent', 'accepted', 'complete', 'cancelled'];
			}
		} else if (this.app.routeData.module === 'sales-order') {
			return ['in_progress', 'complete', 'cancelled'];
		}
	}

	stageColor(stage) {
		switch (stage) {
			case 'cancelled': return 'danger';
			case 'in_progress': return 'secondary';
			case 'in_review': return 'warning';
			case 'passed_review': return 'info';
			case 'sent': return 'purple';
			case 'accepted': return 'success';
			case 'complete': return 'success';
		}
		return 'secondary';
	}

	stageDescription(stage) {
		switch (stage) {
			case 'cancelled': return 'Cancelled';
			case 'in_progress': return 'In Progress';
			case 'in_review': return 'In Review';
			case 'passed_review': return 'Passed Review';
			case 'sent': return 'Sent';
			case 'accepted': return 'Accepted';
			case 'complete': return 'Complete';
		}
		return '';
	}

	reviewResult() {
		this.reviewResultModal.open({ id: this.id, approveBlocked: this.data?.approve_blocked });
	}

	chooseProjectModalClosed(data) {
		if (!data) {
			this.data.set_project_counter += 1;
			this.api.quote.updateProjectCounter({ id: this.id, count: this.data.set_project_counter }, null);
		} else {
			const previous = this.data.project_id;
			this.data.project_id = data;
			sendDealProjectChange(this.app, this.api.quote.setProject, { id: this.id, project_id: data }, () => {
				this.app.quoteStageChange.emit();
			}, error => {
				this.data.project_id = previous;
				this.app.notifications.showDanger(error.message);
			}, () => {
				this.data.project_id = previous;
			});
		}
	}

}
